import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";

export type ChallengeInvite = {
  id: string;
  code: string;
  examTitle: string;
  mockType: string;
  fromName: string | null;
  fromAvatar: string | null;
  createdAt: string;
};

const KEY = ["challenge-invites"] as const;

/** Open challenge invites from other students — shared by the bell and the Bellashuv page. */
export function useChallengeInvites(enabled = true) {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<{ invites: ChallengeInvite[] }>("/api/v1/challenges/invites"),
    enabled,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** Accept or decline; the invite disappears from the list right away. */
export function useRespondToInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      api.post<{ code: string; accepted: boolean }>(`/api/v1/challenges/invites/${id}/respond`, { accept }),
    onMutate: ({ id }) => {
      queryClient.setQueryData<{ invites: ChallengeInvite[] }>(KEY, (current) =>
        current ? { invites: current.invites.filter((i) => i.id !== id) } : current,
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: KEY });
      void queryClient.invalidateQueries({ queryKey: ["challenges", "mine"] });
    },
  });
}
